/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import {
  AnimatePresence,
  motion,
} from "framer-motion";
import { Component } from "react";

const DEFAULT_TIMEOUT = 4000;

interface IProps {
  imageAlts: string[];
  imageJpegs: string[];
  imageWebps: string[];
  timeout?: number;
}

interface IState {
  imageId: number;
}

export class Slideshow extends Component<IProps, IState> {
  private intervalId: NodeJS.Timeout;

  public constructor(props: IProps) {
    super(props);

    this.state = {
      imageId: 0,
    };
  }

  public componentDidMount() {
    const {
      imageJpegs,
      timeout,
    } = this.props;

    this.intervalId = setInterval(() => {
      const { imageId } = this.state;

      this.setState({
        imageId: (imageId + 1) % imageJpegs.length,
      });
    }, timeout ?? DEFAULT_TIMEOUT);
  }

  public componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  public render() {
    const {
      imageAlts,
      imageJpegs,
      imageWebps,
    } = this.props;
    const { imageId } = this.state;

    // Precaches the next image and sets the slideshow size.
    const nextId = (imageId + 1) % imageJpegs.length;

    return (
      <>
        <style jsx={true}>
          {`
            svg {
              height: 20px;
              width: 20px;
            }

            .ls-circle {
              fill: transparent;
            }

            .ls-active,
            .ls-circle:hover {
              cursor: pointer;
              fill: black;
            }
          `}
        </style>
        <div className="relative text-center">
          <picture>
            <source
              srcSet={imageWebps[nextId]}
              type="image/webp"
            />
            <source
              srcSet={imageJpegs[nextId]}
              type="image/jpeg"
            />
            <img
              alt={imageAlts[nextId]}
              className="block left-0 h-auto opacity-0 top-0 w-full"
              loading="lazy"
              src={imageJpegs[nextId]}
            />
          </picture>
          <span>{" "}</span>
          <AnimatePresence
            exitBeforeEnter={true}
            initial={false}
          >
            <picture>
              <source
                srcSet={imageWebps[imageId]}
                type="image/webp"
              />
              <source
                srcSet={imageJpegs[imageId]}
                type="image/jpeg"
              />
              <motion.img
                alt={imageAlts[imageId]}
                animate={{ opacity: 1 }}
                className="absolute block left-0 rounded top-0 w-full"
                initial={{ opacity: 0 }}
                key={imageId}
                src={imageJpegs[imageId]}
                exit={{ opacity: 0 }}
              />
            </picture>
          </AnimatePresence>
        </div>
        <div className="text-center">
          {imageJpegs.map((value: string, index: number) => (
            <svg
              className="inline-block"
              key={index}
              onClick={() => { this.jumpTo(index); }}
              viewBox="0 0 20 20"
            >
              <circle
                className={(imageId === index ? "ls-active " : "") + "ls-circle stroke-current stroke-2"}
                cx="10"
                cy="10"
                r="5"
              />
            </svg>
          ))}
        </div>
      </>
    );
  }

  private readonly jumpTo = (imageId: number) => {
    clearInterval(this.intervalId);
    this.setState({
      imageId,
    });
  }
}
